import { useEffect, useState } from 'react'
import {
  BasicInformatioTableDef,
  TableDef,
  TableRowDef,
} from '../../../../../../containers/SingleSheetV2/table/model'
import {
  TableHeaderWithLayout,
  TableCellWithLayout,
  DecomposedTableDef,
} from '../model'
import { useEntitySearchReferenceEntityRepositories } from '../../../../../../hooks/useWbsItemAdditionalProperties'
import { UPDATED_INFO_TABLE_HEADER_EXTERNAL_ID } from '../table-header-def/wbsItem'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import {
  FunctionPropertyConfigurationMapByExternalId,
  FunctionPropertyConfigurationState,
} from '../../../../hooks/functionPropertyConfiguration'
import { wbsItemAdditionalPropertyToTableDef } from './wbs-item-additional-property'
import {
  overwriteTableCellWithConfig,
  overwriteTableHeaderWithConfig,
} from './function-property-configuration'

const UPDATED_INFO_TABLE_INDEX = 999
const WBS_ITEM_ADDITIONAL_PROPERTY_TABLE_INDEX = 100

export const useBasicInformationTableComposer = (
  projectUuid: string,
  defaultTableDef: BasicInformatioTableDef,
  wbsItemAdditionalProperties:
    | WbsItemAdditionalPropertyLayoutEntity
    | undefined,
  propertyConfiguration: FunctionPropertyConfigurationState
): BasicInformatioTableDef => {
  const [basicInformationTableDef, setBasicInformationTableDef] =
    useState<BasicInformatioTableDef>(defaultTableDef)
  const { getEntitySearchReferenceEntityRepository } =
    useEntitySearchReferenceEntityRepositories(projectUuid)

  useEffect(() => {
    if (!propertyConfiguration.initialized) {
      return
    }
    const configMapByExternalId = propertyConfiguration.configurations

    // Decompose default table temporarily to re-compose table with additional properties, entity extension and table config by user.
    const { tableHeaders: defaultTableHeaders, cells: defaultCells } =
      decomposeDefaultTable(defaultTableDef, configMapByExternalId)
    const {
      tableHeaders: additionalPropertyTableHeaders,
      cells: additionalPropertyCells,
    } = wbsItemAdditionalPropertyToTableDef(
      wbsItemAdditionalProperties,
      configMapByExternalId,
      getEntitySearchReferenceEntityRepository,
      WBS_ITEM_ADDITIONAL_PROPERTY_TABLE_INDEX
    )

    const tableHeaders = [
      ...defaultTableHeaders,
      ...additionalPropertyTableHeaders,
    ]
    const cells = [...defaultCells, ...additionalPropertyCells]
    const tableRowsMapByHeaderExternalId: TableRowsMapByHeaderExtenalId = {}

    cells.forEach(cell => {
      if (cell.hidden) return

      let tableRowMapByRowIndex =
        tableRowsMapByHeaderExternalId[cell.tableHeaderExternalId]
      if (!tableRowMapByRowIndex) {
        tableRowMapByRowIndex = {}
        tableRowsMapByHeaderExternalId[cell.tableHeaderExternalId] =
          tableRowMapByRowIndex
      }
      if (cell.rowIndex in tableRowMapByRowIndex) {
        tableRowMapByRowIndex[cell.rowIndex].push(cell)
      } else {
        tableRowMapByRowIndex[cell.rowIndex] = [cell]
      }
    })

    const composedTables: TableDef[] = []
    tableHeaders.sort((a, b) => a.tableIndex - b.tableIndex)
    tableHeaders.forEach(({ tableHeader, hidden }) => {
      if (hidden) return

      const table: TableDef = {
        header: tableHeader,
        rows: [],
      }
      const tableRowMapByRowIndex =
        tableRowsMapByHeaderExternalId[tableHeader.externalId]
      if (!tableRowMapByRowIndex) return

      const rowIndexes = Object.keys(tableRowMapByRowIndex)
        .map(v => Number(v))
        .sort((a, b) => a - b)
      if (rowIndexes.length === 0) return

      rowIndexes.forEach(rowIndex => {
        const cells = tableRowMapByRowIndex[rowIndex]
        if (cells.length >= 1) {
          table.rows.push(
            cells
              .sort((a, b) => a.columnIndex - b.columnIndex)
              .map(v => v.cell) as TableRowDef
          )
        }
      })

      if (table.rows.length >= 1) {
        composedTables.push(table)
      }
    })

    setBasicInformationTableDef({
      tables: composedTables,
    })
  }, [
    defaultTableDef,
    wbsItemAdditionalProperties,
    getEntitySearchReferenceEntityRepository,
    propertyConfiguration,
  ])

  return basicInformationTableDef
}

type TableRowsMapByHeaderExtenalId = {
  [tableHeaderExternalId: string]: TableRowMapByRowIndex
}
type TableRowMapByRowIndex = {
  [rowIndex: number]: TableCellWithLayout[]
}
const decomposeDefaultTable = (
  defaultTable: BasicInformatioTableDef,
  tableConfigByUserMapByExternalId: FunctionPropertyConfigurationMapByExternalId
): DecomposedTableDef => {
  const tableHeaders: TableHeaderWithLayout[] = []
  const cells: TableCellWithLayout[] = []
  defaultTable.tables.forEach(({ header: defaultHeader, rows }, tableIndex) => {
    const tableHeaderWithLayout = {
      tableHeader: defaultHeader,
      tableIndex:
        defaultHeader.externalId === UPDATED_INFO_TABLE_HEADER_EXTERNAL_ID
          ? UPDATED_INFO_TABLE_INDEX // To show "updated info" table at the last.
          : tableIndex,
      hidden: false,
    }
    if (defaultHeader.externalId in tableConfigByUserMapByExternalId) {
      overwriteTableHeaderWithConfig(
        tableHeaderWithLayout,
        tableConfigByUserMapByExternalId[defaultHeader.externalId]
      )
    }
    tableHeaders.push(tableHeaderWithLayout)

    rows.forEach((defaultCells, rowIndex) => {
      defaultCells.forEach((defaultCell, columnIndex) => {
        const cellWithLayout = {
          cell: defaultCell,
          tableHeaderExternalId: defaultHeader.externalId,
          rowIndex,
          columnIndex,
          hidden: Boolean(defaultCell.hidden),
        }
        if (defaultCell.externalId in tableConfigByUserMapByExternalId) {
          overwriteTableCellWithConfig(
            cellWithLayout,
            tableConfigByUserMapByExternalId[defaultCell.externalId]
          )
        }
        cells.push(cellWithLayout)
      })
    })
  })
  return {
    tableHeaders,
    cells,
  }
}
